import { ApiClient } from './api';
import { UI } from './ui';

// 전역 변수
let apiClient: ApiClient;
let ui: UI;

// API 기본 URL
const API_BASE_URL = 'https://form2ai2email-worker.kilos-network.workers.dev';
// const API_BASE_URL = 'http://localhost:8787'; // 개발용

// 현재 활성 탭
let currentTab = 'dashboard';

// 로그인 화면 표시 및 인증 처리
function showLoginScreen(): Promise<void> {
  return new Promise((resolve) => {
    const loginScreen = document.getElementById('login-screen')!;
    const appContent = document.getElementById('app-content')!;
    const submitBtn = document.getElementById('login-submit')!;
    const submitText = document.getElementById('login-submit-text')!;
    const submitLoading = document.getElementById('login-submit-loading')!;
    const errorMsg = document.getElementById('login-error')!;
    const keyInput = document.getElementById('login-api-key') as HTMLInputElement;
    const toggleBtn = document.getElementById('login-toggle-visibility')!;

    // 비밀번호 보기/숨기기 토글
    toggleBtn.addEventListener('click', () => {
      const isPassword = keyInput.type === 'password';
      keyInput.type = isPassword ? 'text' : 'password';
      toggleBtn.innerHTML = isPassword
        ? '<i class="fas fa-eye-slash text-sm"></i>'
        : '<i class="fas fa-eye text-sm"></i>';
    });

    const attemptLogin = async () => {
      const key = keyInput.value.trim();
      if (!key) {
        errorMsg.textContent = 'API 키를 입력해주세요.';
        errorMsg.classList.remove('hidden');
        return;
      }

      // 로딩 상태
      submitText.classList.add('hidden');
      submitLoading.classList.remove('hidden');
      submitBtn.setAttribute('disabled', 'true');
      errorMsg.classList.add('hidden');

      try {
        // API 키 검증: /api/admin/stats 호출
        apiClient.setAdminApiKey(key);
        const testRes = await apiClient.getStats();

        if (testRes.success) {
          localStorage.setItem('admin_api_key', key);
          loginScreen.style.display = 'none';
          appContent.style.display = 'block';
          resolve();
        } else {
          apiClient.clearAdminApiKey();
          errorMsg.textContent = '잘못된 API 키입니다.';
          errorMsg.classList.remove('hidden');
        }
      } catch (_) {
        apiClient.clearAdminApiKey();
        errorMsg.textContent = '서버 연결에 실패했습니다.';
        errorMsg.classList.remove('hidden');
      } finally {
        submitText.classList.remove('hidden');
        submitLoading.classList.add('hidden');
        submitBtn.removeAttribute('disabled');
      }
    };

    submitBtn.addEventListener('click', attemptLogin);
    keyInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') attemptLogin();
    });

    // 저장된 키가 있으면 자동 로그인 시도
    const savedKey = localStorage.getItem('admin_api_key');
    if (savedKey) {
      keyInput.value = savedKey;
      attemptLogin();
    } else {
      keyInput.focus();
    }
  });
}

// 관리자 설정 모달 표시
async function showAdminSettings() {
  const currentKey = localStorage.getItem('admin_api_key') || '';

  // Discord 설정 현황 조회
  let discordStatus = '';
  let discordSet = false;
  try {
    const settingsRes = await apiClient.getSettings();
    if (settingsRes.success && settingsRes.data) {
      discordSet = settingsRes.data.discord_webhook_set;
      discordStatus = discordSet
        ? `<span class="text-green-600 text-xs"><i class="fas fa-check-circle mr-1"></i>설정됨 (${settingsRes.data.discord_webhook_url || '환경변수'})</span>`
        : `<span class="text-gray-400 text-xs"><i class="fas fa-times-circle mr-1"></i>미설정</span>`;
    }
  } catch (_) {}

  ui.showModal('관리자 설정', `
    <div class="space-y-6">
      <!-- API 키 -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">관리자 API 키</label>
        <input type="password" id="admin-api-key-input" value="${currentKey}"
               class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               placeholder="관리자 API 키를 입력하세요">
        <p class="mt-1 text-xs text-gray-500">관리자 API 접근을 위한 보안 키입니다.</p>
        <div class="flex justify-end space-x-2 mt-3">
          <button id="admin-key-clear" class="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm">삭제</button>
          <button id="admin-key-save" class="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">저장</button>
        </div>
      </div>

      <hr class="border-gray-200">

      <!-- Discord Webhook -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="block text-sm font-medium text-gray-700">Discord Webhook URL</label>
          ${discordStatus}
        </div>
        <input type="text" id="discord-webhook-input"
               class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
               placeholder="https://discord.com/api/webhooks/...">
        <p class="mt-1 text-xs text-gray-500">이메일 전송 실패 시 Discord로 알림을 받습니다. 30분에 1번 발송됩니다.</p>
        <div class="flex justify-end space-x-2 mt-3">
          ${discordSet ? `<button id="discord-webhook-clear" class="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm">삭제</button>` : ''}
          <button id="discord-webhook-save" class="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm">
            <i class="fab fa-discord mr-1"></i>저장
          </button>
        </div>
      </div>

      <div class="flex justify-end pt-2">
        <button id="admin-settings-cancel" class="px-4 py-2 text-gray-600 hover:text-gray-800">닫기</button>
      </div>
    </div>
  `);

  document.getElementById('admin-settings-cancel')?.addEventListener('click', ui.hideModal);

  // API 키 저장
  document.getElementById('admin-key-save')?.addEventListener('click', () => {
    const input = document.getElementById('admin-api-key-input') as HTMLInputElement;
    if (input?.value.trim()) {
      localStorage.setItem('admin_api_key', input.value.trim());
      apiClient.setAdminApiKey(input.value.trim());
      ui.showToast('관리자 API 키가 저장되었습니다.', 'success');
    } else {
      ui.showToast('유효한 API 키를 입력해주세요.', 'error');
    }
  });

  // API 키 삭제
  document.getElementById('admin-key-clear')?.addEventListener('click', () => {
    if (confirm('관리자 API 키를 삭제하시겠습니까?')) {
      localStorage.removeItem('admin_api_key');
      apiClient.clearAdminApiKey();
      ui.showToast('관리자 API 키가 삭제되었습니다.', 'info');
      ui.hideModal();
    }
  });

  // Discord webhook 저장
  document.getElementById('discord-webhook-save')?.addEventListener('click', async () => {
    const input = document.getElementById('discord-webhook-input') as HTMLInputElement;
    const url = input?.value.trim();
    if (!url) {
      ui.showToast('Webhook URL을 입력해주세요.', 'error');
      return;
    }
    if (!url.startsWith('https://discord.com/api/webhooks/')) {
      ui.showToast('올바른 Discord Webhook URL을 입력해주세요.', 'error');
      return;
    }
    const res = await apiClient.updateSettings({ discord_webhook_url: url });
    if (res.success) {
      ui.showToast('Discord Webhook이 저장되었습니다.', 'success');
      ui.hideModal();
    } else {
      ui.showToast(res.error || '저장 실패', 'error');
    }
  });

  // Discord webhook 삭제
  document.getElementById('discord-webhook-clear')?.addEventListener('click', async () => {
    if (confirm('Discord Webhook을 삭제하시겠습니까?')) {
      const res = await apiClient.updateSettings({ discord_webhook_url: '' });
      if (res.success) {
        ui.showToast('Discord Webhook이 삭제되었습니다.', 'info');
        ui.hideModal();
      } else {
        ui.showToast(res.error || '삭제 실패', 'error');
      }
    }
  });
}

// 애플리케이션 초기화
async function initApp() {
  apiClient = new ApiClient(API_BASE_URL);
  ui = new UI();

  // 로그인 화면 처리 (API 키 없으면 입력 요구)
  await showLoginScreen();

  // 이벤트 리스너 설정
  setupEventListeners();

  // 로그아웃 버튼
  document.getElementById('logout-btn')?.addEventListener('click', () => {
    localStorage.removeItem('admin_api_key');
    apiClient.clearAdminApiKey();
    location.reload();
  });

  // 초기 UUID 생성
  generateNewUUID();

  // 대시보드 로드
  await loadDashboard();
}

// 이벤트 리스너 설정
function setupEventListeners() {
  // 탭 전환
  document.querySelectorAll('.tab-button').forEach(button => {
    button.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const tabId = target.id.replace('tab-', '');
      switchTab(tabId);
    });
  });

  // UUID 생성
  document.getElementById('generate-uuid')?.addEventListener('click', generateNewUUID);
  
  // 폼 생성
  document.getElementById('create-form')?.addEventListener('click', handleCreateForm);
  
  // 새로고침 버튼들
  document.getElementById('refresh-forms')?.addEventListener('click', () => loadForms());
  document.getElementById('refresh-unconnected')?.addEventListener('click', () => loadUnconnectedForms());
  document.getElementById('refresh-submissions')?.addEventListener('click', () => loadSubmissions());
  document.getElementById('refresh-emails')?.addEventListener('click', () => loadEmailLogs());
  
  // 폼 필터
  document.getElementById('filter-form')?.addEventListener('change', () => loadSubmissions());
  
  // 모달 닫기
  document.getElementById('modal-close')?.addEventListener('click', ui.hideModal);
  document.getElementById('modal')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) {
      ui.hideModal();
    }
  });

  // 토스트 닫기
  document.getElementById('toast-close')?.addEventListener('click', ui.hideToast);
  
  // 관리자 설정
  document.getElementById('admin-settings')?.addEventListener('click', showAdminSettings);
}

// UUID 생성
function generateNewUUID(): string {
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
  
  const formIdInput = document.getElementById('form-id') as HTMLInputElement;
  if (formIdInput) {
    formIdInput.value = uuid;
  }
  
  return uuid;
}

// 탭 전환
function switchTab(tabId: string) {
  // 모든 탭 버튼 비활성화
  document.querySelectorAll('.tab-button').forEach(button => {
    button.classList.remove('active', 'border-blue-500', 'text-blue-600');
    button.classList.add('border-transparent', 'text-gray-500');
  });

  // 모든 탭 콘텐츠 숨기기
  document.querySelectorAll('.tab-content').forEach(content => {
    content.classList.add('hidden');
  });

  // 선택된 탭 활성화
  const selectedButton = document.getElementById(`tab-${tabId}`);
  if (selectedButton) {
    selectedButton.classList.add('active', 'border-blue-500', 'text-blue-600');
    selectedButton.classList.remove('border-transparent', 'text-gray-500');
  }

  // 선택된 콘텐츠 표시
  const selectedContent = document.getElementById(`content-${tabId}`);
  if (selectedContent) {
    selectedContent.classList.remove('hidden');
  }

  currentTab = tabId;

  // 탭별 데이터 로드
  switch (tabId) {
    case 'dashboard':
      loadDashboard();
      break;
    case 'forms':
      loadForms();
      break;
    case 'unconnected':
      loadUnconnectedForms();
      break;
    case 'submissions':
      loadSubmissions();
      break;
    case 'emails':
      loadEmailLogs();
      break;
  }
}

// 대시보드 로드
async function loadDashboard() {
  try {
    ui.showLoading(true, '대시보드를 로드하는 중...');
    
    // 통계 데이터 로드
    const stats = await apiClient.getStats();
    if (stats.success) {
      updateStats(stats.data);
    }

    // 최근 활동 로드
    const activity = await apiClient.getRecentActivity();
    if (activity.success && activity.data) {
      renderRecentActivity(activity.data);
    }

    ui.showLoading(false);
  } catch (error) {
    console.error('Dashboard load error:', error);
    ui.showToast('대시보드 로드 중 오류가 발생했습니다.', 'error');
    ui.showLoading(false);
  }
}

// 통계 업데이트
function updateStats(stats: any) {
  const elements = {
    'stats-total-forms': stats.totalForms || 0,
    'stats-connected-forms': stats.connectedForms || 0,
    'stats-unconnected-forms': stats.unconnectedForms || 0,
    'stats-total-submissions': stats.totalSubmissions || 0
  };

  Object.entries(elements).forEach(([id, value]) => {
    const element = document.getElementById(id);
    if (element) {
      element.textContent = value.toString();
    }
  });
}

// 최근 활동 렌더링
function renderRecentActivity(activities: any[]) {
  const container = document.getElementById('recent-activity');
  if (!container) return;

  if (activities.length === 0) {
    container.innerHTML = `
      <div class="text-center text-gray-500 py-8">
        <i class="fas fa-clock text-4xl mb-4"></i>
        <p>최근 활동이 없습니다.</p>
      </div>
    `;
    return;
  }

  container.innerHTML = activities.map(activity => `
    <div class="flex items-center p-4 bg-gray-50 rounded-lg">
      <div class="flex-shrink-0">
        <i class="fas ${getActivityIcon(activity.type)} text-blue-600"></i>
      </div>
      <div class="ml-4 flex-1">
        <p class="text-sm font-medium text-gray-900">${activity.message}</p>
        <p class="text-xs text-gray-500">${formatDate(activity.created_at)}</p>
      </div>
    </div>
  `).join('');
}

// 활동 아이콘 가져오기
function getActivityIcon(type: string): string {
  switch (type) {
    case 'form_created': return 'fa-plus-circle';
    case 'form_submitted': return 'fa-paper-plane';
    case 'email_sent': return 'fa-envelope';
    case 'form_connected': return 'fa-link';
    default: return 'fa-info-circle';
  }
}

// 폼 생성 처리
async function handleCreateForm() {
  const formId = (document.getElementById('form-id') as HTMLInputElement).value.trim();
  const name = (document.getElementById('form-name') as HTMLInputElement).value.trim();
  const email = (document.getElementById('form-email') as HTMLInputElement).value.trim();
  const domain = (document.getElementById('form-domain') as HTMLInputElement).value.trim();

  if (!formId || !name) {
    ui.showToast('폼 ID와 이름을 입력해주세요.', 'error');
    return;
  }

  try {
    ui.showLoading(true, '폼을 생성하는 중...');

    const response = await apiClient.createForm({
      form_id: formId,
      name: name,
      target_email: email || null,
      allowed_domain: domain || null
    });

    if (response.success) {
      ui.showToast('폼이 성공적으로 생성되었습니다!', 'success');
      
      // 폼 초기화
      (document.getElementById('form-name') as HTMLInputElement).value = '';
      (document.getElementById('form-email') as HTMLInputElement).value = '';
      (document.getElementById('form-domain') as HTMLInputElement).value = '';
      generateNewUUID();

      // 사용법 모달 표시
      showFormUsageModal(formId);

      // 대시보드 새로고침
      await loadDashboard();
    } else {
      ui.showToast(response.error || '폼 생성에 실패했습니다.', 'error');
    }

    ui.showLoading(false);
  } catch (error) {
    console.error('Form creation error:', error);
    ui.showToast('폼 생성 중 오류가 발생했습니다.', 'error');
    ui.showLoading(false);
  }
}

// 폼 사용법 모달 표시
function showFormUsageModal(formId: string) {
  const endpoint = `${API_BASE_URL}/api/submit`;
  
  ui.showModal('폼 생성 완료', `
    <div class="space-y-6">
      <div class="bg-green-50 border border-green-200 rounded-md p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <i class="fas fa-check-circle text-green-400"></i>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-green-800">폼이 성공적으로 생성되었습니다!</h3>
            <div class="mt-2 text-sm text-green-700">
              <p>아래 정보를 사용하여 웹사이트에 폼을 연동하세요.</p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">폼 ID</label>
        <div class="flex items-center space-x-2">
          <input type="text" value="${formId}" readonly 
                 class="flex-1 border border-gray-300 rounded-md px-3 py-2 bg-gray-50 font-mono text-sm"
                 id="modal-form-id">
          <button onclick="navigator.clipboard.writeText('${formId}')" 
                  class="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            <i class="fas fa-copy"></i>
          </button>
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">API 엔드포인트</label>
        <div class="flex items-center space-x-2">
          <input type="text" value="${endpoint}" readonly 
                 class="flex-1 border border-gray-300 rounded-md px-3 py-2 bg-gray-50 font-mono text-sm"
                 id="modal-endpoint">
          <button onclick="navigator.clipboard.writeText('${endpoint}')" 
                  class="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            <i class="fas fa-copy"></i>
          </button>
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">JavaScript 예제</label>
        <pre class="bg-gray-900 text-green-400 p-4 rounded-md text-sm overflow-x-auto"><code>// 폼 제출 예제
fetch('${endpoint}', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    form_id: '${formId}',
    data: {
      name: '홍길동',
      email: 'user@example.com',
      message: '문의 내용'
    }
  })
})
.then(response => response.json())
.then(data => {
  if (data.success) {
    alert('제출 완료!');
  } else {
    alert('오류: ' + data.error);
  }
});</code></pre>
      </div>

      <div class="flex space-x-3">
        <button onclick="app.ui.hideModal(); app.switchTab('forms');" 
                class="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700">
          폼 관리로 이동
        </button>
        <button onclick="app.ui.hideModal()" 
                class="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700">
          닫기
        </button>
      </div>
    </div>
  `);
}

// 폼 목록 로드
async function loadForms() {
  try {
    ui.showLoading(true, '폼 목록을 로드하는 중...');
    
    const response = await apiClient.getForms();
    if (response.success && response.data) {
      renderFormsList(response.data.forms);
    }

    ui.showLoading(false);
  } catch (error) {
    console.error('Load forms error:', error);
    ui.showToast('폼 목록 로드 중 오류가 발생했습니다.', 'error');
    ui.showLoading(false);
  }
}

// 폼 목록 렌더링
function renderFormsList(forms: any[]) {
  const container = document.getElementById('forms-list');
  const emptyContainer = document.getElementById('forms-empty');
  
  if (!container || !emptyContainer) return;

  if (forms.length === 0) {
    container.classList.add('hidden');
    emptyContainer.classList.remove('hidden');
    return;
  }

  container.classList.remove('hidden');
  emptyContainer.classList.add('hidden');

  container.innerHTML = forms.map(form => `
    <div class="border border-gray-200 rounded-lg p-6">
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <h3 class="text-lg font-semibold text-gray-900">${form.name}</h3>
          <p class="text-sm text-gray-600 font-mono">${form.form_id}</p>
          <div class="mt-2 flex items-center space-x-4 text-sm text-gray-500">
            <span><i class="fas fa-calendar mr-1"></i>${formatDate(form.created_at)}</span>
            <span><i class="fas fa-paper-plane mr-1"></i>${form.submission_count || 0}회 제출</span>
            ${form.target_email ? 
              `<span class="text-green-600"><i class="fas fa-envelope mr-1"></i>${form.target_email}</span>` : 
              `<span class="text-yellow-600"><i class="fas fa-exclamation-triangle mr-1"></i>이메일 미연결</span>`
            }
          </div>
        </div>
        <div class="flex space-x-2">
          <button onclick="viewFormDetails('${form.form_id}')" 
                  class="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
            <i class="fas fa-eye mr-1"></i>상세
          </button>
          <button onclick="copyFormCode('${form.form_id}')" 
                  class="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm">
            <i class="fas fa-code mr-1"></i>코드
          </button>
          ${!form.target_email ? 
            `<button onclick="connectEmail('${form.form_id}')" 
                     class="px-3 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm">
              <i class="fas fa-link mr-1"></i>연결
            </button>` : ''
          }
        </div>
      </div>
    </div>
  `).join('');
}

// 미연결 폼 로드
async function loadUnconnectedForms() {
  try {
    ui.showLoading(true, '미연결 폼을 로드하는 중...');
    
    const response = await apiClient.getUnconnectedForms();
    if (response.success && response.data) {
      renderUnconnectedForms(response.data.forms);
    }

    ui.showLoading(false);
  } catch (error) {
    console.error('Load unconnected forms error:', error);
    ui.showToast('미연결 폼 로드 중 오류가 발생했습니다.', 'error');
    ui.showLoading(false);
  }
}

// 미연결 폼 렌더링
function renderUnconnectedForms(forms: any[]) {
  const container = document.getElementById('unconnected-list');
  const emptyContainer = document.getElementById('unconnected-empty');
  
  if (!container || !emptyContainer) return;

  if (forms.length === 0) {
    container.classList.add('hidden');
    emptyContainer.classList.remove('hidden');
    return;
  }

  container.classList.remove('hidden');
  emptyContainer.classList.add('hidden');

  container.innerHTML = forms.map(form => `
    <div class="border border-yellow-200 bg-yellow-50 rounded-lg p-6">
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <h3 class="text-lg font-semibold text-gray-900">${form.name}</h3>
          <p class="text-sm text-gray-600 font-mono">${form.form_id}</p>
          <div class="mt-2 flex items-center space-x-4 text-sm text-gray-500">
            <span><i class="fas fa-calendar mr-1"></i>${formatDate(form.created_at)}</span>
            <span><i class="fas fa-paper-plane mr-1"></i>${form.submission_count || 0}회 제출</span>
            <span class="text-yellow-600"><i class="fas fa-exclamation-triangle mr-1"></i>이메일 미연결</span>
          </div>
        </div>
        <div class="flex space-x-2">
          <button onclick="connectEmail('${form.form_id}')" 
                  class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            <i class="fas fa-link mr-1"></i>이메일 연결
          </button>
        </div>
      </div>
    </div>
  `).join('');
}

// 제출 이력 로드
async function loadSubmissions() {
  try {
    ui.showLoading(true, '제출 이력을 로드하는 중...');
    
    const filterForm = document.getElementById('filter-form') as HTMLSelectElement;
    const formId = filterForm?.value || null;
    
    const response = await apiClient.getSubmissions(formId);
    if (response.success && response.data) {
      renderSubmissions(response.data.submissions);
      updateFormFilter(response.data.forms || []);
    }

    ui.showLoading(false);
  } catch (error) {
    console.error('Load submissions error:', error);
    ui.showToast('제출 이력 로드 중 오류가 발생했습니다.', 'error');
    ui.showLoading(false);
  }
}

// 제출 이력 렌더링
function renderSubmissions(submissions: any[]) {
  const container = document.getElementById('submissions-list');
  const emptyContainer = document.getElementById('submissions-empty');
  
  if (!container || !emptyContainer) return;

  if (submissions.length === 0) {
    container.classList.add('hidden');
    emptyContainer.classList.remove('hidden');
    return;
  }

  container.classList.remove('hidden');
  emptyContainer.classList.add('hidden');

  container.innerHTML = submissions.map(submission => `
    <div class="border border-gray-200 rounded-lg p-6">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">${submission.form_name}</h3>
          <p class="text-sm text-gray-600 font-mono">${submission.form_id}</p>
        </div>
        <div class="text-right">
          <p class="text-sm text-gray-500">${formatDate(submission.created_at)}</p>
          <p class="text-xs text-gray-400">IP: ${submission.client_ip || 'N/A'}</p>
        </div>
      </div>
      
      <div class="bg-gray-50 rounded-md p-4">
        <h4 class="text-sm font-medium text-gray-700 mb-2">제출 데이터</h4>
        <pre class="text-xs text-gray-600 whitespace-pre-wrap">${JSON.stringify(JSON.parse(submission.form_data), null, 2)}</pre>
      </div>
      
      <div class="mt-4 flex justify-end">
        <button onclick="viewSubmissionDetails('${submission.id}')" 
                class="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
          <i class="fas fa-eye mr-1"></i>상세 보기
        </button>
      </div>
    </div>
  `).join('');
}

// 이메일 로그 로드
async function loadEmailLogs() {
  try {
    ui.showLoading(true, '이메일 로그를 로드하는 중...');
    
    const response = await apiClient.getEmailLogs();
    if (response.success && response.data) {
      renderEmailLogs(response.data.logs);
    }

    ui.showLoading(false);
  } catch (error) {
    console.error('Load email logs error:', error);
    ui.showToast('이메일 로그 로드 중 오류가 발생했습니다.', 'error');
    ui.showLoading(false);
  }
}

// 이메일 로그 렌더링
function renderEmailLogs(logs: any[]) {
  const container = document.getElementById('emails-list');
  const emptyContainer = document.getElementById('emails-empty');
  
  if (!container || !emptyContainer) return;

  if (logs.length === 0) {
    container.classList.add('hidden');
    emptyContainer.classList.remove('hidden');
    return;
  }

  container.classList.remove('hidden');
  emptyContainer.classList.add('hidden');

  container.innerHTML = logs.map(log => `
    <div class="border border-gray-200 rounded-lg p-6">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">${log.subject}</h3>
          <p class="text-sm text-gray-600">받는 사람: ${log.to_email}</p>
        </div>
        <div class="text-right">
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            log.status === 'sent' ? 'bg-green-100 text-green-800' : 
            log.status === 'failed' ? 'bg-red-100 text-red-800' : 
            'bg-yellow-100 text-yellow-800'
          }">
            ${log.status === 'sent' ? '전송 완료' : 
              log.status === 'failed' ? '전송 실패' : '전송 중'}
          </span>
          <p class="text-sm text-gray-500 mt-1">${formatDate(log.created_at)}</p>
        </div>
      </div>
      
      <div class="text-sm text-gray-600">
        <p><strong>폼:</strong> ${log.form_name} (${log.form_id})</p>
        ${log.error_message ? `<p class="text-red-600 mt-2"><strong>오류:</strong> ${log.error_message}</p>` : ''}
      </div>
    </div>
  `).join('');
}

// 폼 필터 업데이트
function updateFormFilter(forms: any[]) {
  const filterSelect = document.getElementById('filter-form') as HTMLSelectElement;
  if (!filterSelect) return;

  const currentValue = filterSelect.value;
  filterSelect.innerHTML = '<option value="">모든 폼</option>';
  
  forms.forEach(form => {
    const option = document.createElement('option');
    option.value = form.form_id;
    option.textContent = `${form.name} (${form.form_id.substring(0, 8)}...)`;
    filterSelect.appendChild(option);
  });

  filterSelect.value = currentValue;
}

// 날짜 포맷팅
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// 폼 상세 모달 표시
function showFormDetailModal(data: any) {
  const form = data.form;
  const recentSubmissions = data.recentSubmissions || [];
  const monthlyStats = data.monthlyStats || [];

  // 월별 통계 차트 데이터 준비
  const chartData = monthlyStats.map((stat: any) => `
    <div class="flex justify-between items-center py-2 border-b border-gray-100">
      <span class="text-sm text-gray-600">${stat.month}</span>
      <span class="text-sm font-medium">${stat.count}회</span>
    </div>
  `).join('');

  // 최근 제출 이력
  const submissionsHtml = recentSubmissions.length > 0 ? recentSubmissions.map((submission: any) => {
    const data = JSON.parse(submission.submission_data);
    const preview = Object.entries(data).slice(0, 2).map(([key, value]) => 
      `${key}: ${String(value).substring(0, 30)}${String(value).length > 30 ? '...' : ''}`
    ).join(', ');
    
    return `
      <div class="bg-gray-50 rounded-md p-3 mb-2">
        <div class="flex justify-between items-start mb-1">
          <span class="text-xs text-gray-500">${formatDate(submission.created_at)}</span>
          <span class="text-xs text-gray-400">IP: ${submission.sender_ip}</span>
        </div>
        <p class="text-sm text-gray-700">${preview}</p>
      </div>
    `;
  }).join('') : '<p class="text-sm text-gray-500 text-center py-4">아직 제출된 데이터가 없습니다.</p>';

  ui.showModal('폼 상세 정보', `
    <div class="space-y-6">
      <!-- 기본 정보 -->
      <div class="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div class="flex items-center justify-between mb-3">
          <input type="text" id="form-detail-name" 
                 value="${form.name}" 
                 class="text-lg font-semibold bg-transparent border-none text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1 flex-1"
                 placeholder="폼 이름을 입력하세요">
          <button onclick="updateFormInfoFromModal('${form.form_id}')" 
                  class="ml-2 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
            <i class="fas fa-save mr-1"></i>저장
          </button>
        </div>
        <div class="grid grid-cols-2 gap-4 text-sm mb-4">
          <div>
            <span class="text-blue-700 font-medium">폼 ID:</span>
            <p class="font-mono text-blue-800 mt-1">${form.form_id}</p>
          </div>
          <div>
            <span class="text-blue-700 font-medium">생성일:</span>
            <p class="text-blue-800 mt-1">${formatDate(form.created_at)}</p>
          </div>
          <div>
            <span class="text-blue-700 font-medium">총 제출 수:</span>
            <p class="text-blue-800 mt-1 font-semibold">${form.submission_count}회</p>
          </div>
          <div>
            <span class="text-blue-700 font-medium">허용 도메인:</span>
            <p class="text-blue-800 mt-1">${form.allowed_domain || '제한 없음'}</p>
          </div>
        </div>
        
        <!-- 관리자 메모 -->
        <div>
          <label class="block text-sm font-medium text-blue-700 mb-2">관리자 메모</label>
          <div class="flex items-start space-x-2">
            <textarea id="form-detail-notes" 
                      placeholder="이 폼에 대한 메모를 입력하세요..."
                      class="flex-1 border border-blue-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      rows="3">${form.admin_notes || ''}</textarea>
            <button onclick="updateFormInfoFromModal('${form.form_id}')" 
                    class="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
              <i class="fas fa-save"></i>
            </button>
          </div>
          <p class="text-xs text-blue-600 mt-1">관리자만 볼 수 있는 메모입니다.</p>
        </div>
      </div>

      <!-- 폼 설정 -->
      <div class="border border-gray-200 rounded-md p-4">
        <h4 class="text-md font-semibold text-gray-900 mb-3">폼 설정</h4>
        
        <!-- 이메일 설정 -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">관리자 이메일</label>
          <div class="flex items-center space-x-3">
            <input type="email" id="form-detail-email" 
                   value="${form.target_email || ''}" 
                   placeholder="이메일 주소를 입력하세요"
                   class="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm">
            <button onclick="updateFormSettingsFromModal('${form.form_id}')" 
                    class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
              <i class="fas fa-save mr-1"></i>저장
            </button>
          </div>
          <p class="text-xs text-gray-500 mt-1">폼 제출 시 이 이메일로 알림이 전송됩니다.</p>
        </div>
        
        <!-- 허용 도메인 설정 -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">허용 도메인 (CORS)</label>
          <div class="flex items-center space-x-3">
            <input type="text" id="form-detail-domain"
                   value="${form.allowed_domain || ''}"
                   placeholder="https://example.com, https://app.example.com"
                   class="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm">
            <button onclick="updateFormSettingsFromModal('${form.form_id}')"
                    class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm">
              <i class="fas fa-save mr-1"></i>저장
            </button>
          </div>
          <p class="text-xs text-gray-500 mt-1">
            여러 도메인은 쉼표(,)로 구분하세요. 비워두면 모든 도메인에서 접근 가능합니다.<br>
            <span class="text-gray-400">예: https://site1.com, https://site2.com</span>
          </p>
        </div>

        <!-- Discord 웹훅 설정 -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fab fa-discord mr-1 text-indigo-500"></i>Discord 웹훅 (폼별)
          </label>
          <div id="form-discord-status" class="text-xs text-gray-400 mb-2">로딩 중...</div>
          <div class="flex items-center space-x-3">
            <input type="text" id="form-detail-discord"
                   placeholder="https://discord.com/api/webhooks/..."
                   class="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm font-mono">
            <button onclick="saveFormDiscord('${form.form_id}')"
                    class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm">
              <i class="fas fa-save mr-1"></i>저장
            </button>
            <button onclick="deleteFormDiscord('${form.form_id}')"
                    class="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 text-sm">
              <i class="fas fa-trash"></i>
            </button>
          </div>
          <p class="text-xs text-gray-500 mt-1">폼 제출 시 이 Discord 채널로 제출 데이터가 전송됩니다.</p>
        </div>
      </div>

      <!-- 월별 제출 통계 -->
      <div class="border border-gray-200 rounded-md p-4">
        <h4 class="text-md font-semibold text-gray-900 mb-3">월별 제출 통계 (최근 6개월)</h4>
        <div class="space-y-1">
          ${chartData || '<p class="text-sm text-gray-500 text-center py-4">통계 데이터가 없습니다.</p>'}
        </div>
      </div>

      <!-- 최근 제출 이력 -->
      <div class="border border-gray-200 rounded-md p-4">
        <h4 class="text-md font-semibold text-gray-900 mb-3">최근 제출 이력 (최대 10개)</h4>
        <div class="max-h-60 overflow-y-auto">
          ${submissionsHtml}
        </div>
      </div>

      <!-- 액션 버튼 -->
      <div class="flex space-x-3">
        <button onclick="copyFormCode('${form.form_id}')" 
                class="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700">
          <i class="fas fa-code mr-1"></i>코드 복사
        </button>
        <button onclick="app.ui.hideModal(); app.switchTab('submissions');" 
                class="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700">
          <i class="fas fa-list mr-1"></i>전체 제출 이력
        </button>
        <button onclick="app.ui.hideModal()" 
                class="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700">
          닫기
        </button>
      </div>
    </div>
  `);
}

// 전역 함수들 (HTML에서 직접 호출)
(window as any).viewFormDetails = async (formId: string) => {
  try {
    ui.showLoading(true, '폼 상세 정보를 로드하는 중...');

    const response = await apiClient.getFormDetail(formId);
    if (response.success && response.data) {
      showFormDetailModal(response.data);
      // Discord 웹훅 상태 비동기 로드
      apiClient.getFormDiscordWebhook(formId).then(res => {
        const statusEl = document.getElementById('form-discord-status');
        if (!statusEl) return;
        if (res.success && res.data) {
          statusEl.textContent = res.data.discord_webhook_set
            ? `✅ 설정됨 (${res.data.discord_webhook_url})`
            : '미설정';
        }
      });
    } else {
      ui.showToast(response.error || '폼 상세 정보를 불러올 수 없습니다.', 'error');
    }

    ui.showLoading(false);
  } catch (error) {
    console.error('Form detail error:', error);
    ui.showToast('폼 상세 정보 로드 중 오류가 발생했습니다.', 'error');
    ui.showLoading(false);
  }
};

(window as any).saveFormDiscord = async (formId: string) => {
  const input = document.getElementById('form-detail-discord') as HTMLInputElement;
  const url = input?.value.trim();
  if (!url) {
    ui.showToast('웹훅 URL을 입력하세요.', 'error');
    return;
  }
  const response = await apiClient.updateFormDiscordWebhook(formId, url);
  if (response.success) {
    const statusEl = document.getElementById('form-discord-status');
    if (statusEl) statusEl.textContent = `✅ 설정됨 (***${url.slice(-6)})`;
    input.value = '';
    ui.showToast('Discord 웹훅이 저장되었습니다.', 'success');
  } else {
    ui.showToast(response.error || '저장 실패', 'error');
  }
};

(window as any).deleteFormDiscord = async (formId: string) => {
  const response = await apiClient.updateFormDiscordWebhook(formId, null);
  if (response.success) {
    const statusEl = document.getElementById('form-discord-status');
    if (statusEl) statusEl.textContent = '미설정';
    ui.showToast('Discord 웹훅이 삭제되었습니다.', 'success');
  } else {
    ui.showToast(response.error || '삭제 실패', 'error');
  }
};

(window as any).copyFormCode = (formId: string) => {
  const code = `fetch('${API_BASE_URL}/api/submit', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    form_id: '${formId}',
    data: { /* 폼 데이터 */ }
  })
});`;
  navigator.clipboard.writeText(code);
  ui.showToast('코드가 클립보드에 복사되었습니다!', 'success');
};

(window as any).connectEmail = async (formId: string) => {
  const email = prompt('연결할 이메일 주소를 입력하세요:');
  if (email) {
    try {
      const response = await apiClient.connectFormEmail(formId, email);
      if (response.success) {
        ui.showToast('이메일이 성공적으로 연결되었습니다!', 'success');
        if (currentTab === 'unconnected') {
          loadUnconnectedForms();
        } else {
          loadForms();
        }
        loadDashboard(); // 통계 업데이트
      } else {
        ui.showToast(response.error || '이메일 연결에 실패했습니다.', 'error');
      }
    } catch (error) {
      ui.showToast('이메일 연결 중 오류가 발생했습니다.', 'error');
    }
  }
};

// 폼 기본 정보 업데이트 (모달에서)
(window as any).updateFormInfoFromModal = async (formId: string) => {
  try {
    const nameInput = document.getElementById('form-detail-name') as HTMLInputElement;
    const notesInput = document.getElementById('form-detail-notes') as HTMLTextAreaElement;
    
    if (!nameInput || !notesInput) {
      ui.showToast('입력 필드를 찾을 수 없습니다.', 'error');
      return;
    }

    const name = nameInput.value.trim();
    const admin_notes = notesInput.value.trim();

    if (!name) {
      ui.showToast('폼 이름은 필수입니다.', 'error');
      nameInput.focus();
      return;
    }

    const response = await apiClient.updateFormInfo(formId, { 
      name, 
      admin_notes: admin_notes || undefined 
    });
    
    if (response.success) {
      ui.showToast('폼 정보가 성공적으로 업데이트되었습니다!', 'success');
      
      // 현재 탭에 따라 목록 새로고침
      if (currentTab === 'forms') {
        loadForms();
      } else if (currentTab === 'unconnected') {
        loadUnconnectedForms();
      }
      
      // 통계 업데이트 (폼 이름이 변경되었을 수 있음)
      loadDashboard();
      
    } else {
      ui.showToast(response.error || '폼 정보 업데이트에 실패했습니다.', 'error');
    }
  } catch (error) {
    console.error('Update form info error:', error);
    ui.showToast('폼 정보 업데이트 중 오류가 발생했습니다.', 'error');
  }
};

(window as any).viewSubmissionDetails = (submissionId: string) => {
  // 제출 상세 정보 모달 표시
  console.log('View submission details:', submissionId);
};

// 모달에서 폼 설정 업데이트 (이메일 및 허용 도메인)
(window as any).updateFormSettingsFromModal = async (formId: string) => {
  const emailInput = document.getElementById('form-detail-email') as HTMLInputElement;
  const domainInput = document.getElementById('form-detail-domain') as HTMLInputElement;
  
  if (!emailInput || !domainInput) return;

  const email = emailInput.value.trim();
  const allowedDomain = domainInput.value.trim();

  // 이메일과 도메인 모두 비어있으면 오류
  if (!email && !allowedDomain) {
    ui.showToast('이메일 주소 또는 허용 도메인 중 하나는 입력해주세요.', 'error');
    return;
  }

  // 이메일 형식 검증 (이메일이 입력된 경우)
  if (email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      ui.showToast('유효하지 않은 이메일 형식입니다.', 'error');
      return;
    }
  }

  // 도메인 형식 검증 (도메인이 입력된 경우)
  if (allowedDomain) {
    // 여러 도메인을 쉼표로 구분한 경우 처리
    const domains = allowedDomain.split(',').map(d => d.trim()).filter(d => d.length > 0);
    const domainRegex = /^https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    for (const domain of domains) {
      if (!domainRegex.test(domain)) {
        ui.showToast(`유효하지 않은 도메인 형식입니다: "${domain}" (예: https://example.com)`, 'error');
        return;
      }
    }
  }

  try {
    const response = await apiClient.updateFormEmail(
      formId, 
      email || undefined, 
      allowedDomain || undefined
    );
    
    if (response.success) {
      ui.showToast('폼 설정이 성공적으로 업데이트되었습니다!', 'success');
      
      // 대시보드 새로고침
      await loadDashboard();
      
      // 현재 탭에 따라 목록 새로고침
      if (currentTab === 'forms') {
        await loadForms();
      } else if (currentTab === 'unconnected') {
        await loadUnconnectedForms();
      }
    } else {
      ui.showToast(response.error || '이메일 업데이트에 실패했습니다.', 'error');
    }
  } catch (error) {
    console.error('Update email error:', error);
    ui.showToast('이메일 업데이트 중 오류가 발생했습니다.', 'error');
  }
};

// 앱 객체 (UI 접근용)
(window as any).app = {
  get ui() { return ui; },
  switchTab
};

// 앱 초기화
document.addEventListener('DOMContentLoaded', initApp);

export { initApp }; 